/** Helpers for income cycle dashboard (mockup Rendimentos). */

export function isFixedRecurrence(recurrence) {
  return String(recurrence || '').toLowerCase() === 'monthly_fixed';
}

/**
 * Sum of base_amount for active income sources.
 * @param {Array<{ is_active?: boolean; base_amount?: string|number|null }>} sources
 */
export function computePlannedIncome(sources = []) {
  return sources
    .filter((s) => s.is_active !== false)
    .reduce((sum, source) => {
      const amount = Number(source.base_amount || 0);
      return Number.isFinite(amount) ? sum + amount : sum;
    }, 0);
}

/** True when planned income exists and received is still below it. */
export function isBelowPlannedIncome(received, planned) {
  const plannedNum = Number(planned || 0);
  const receivedNum = Number(received || 0);
  return plannedNum > 0 && receivedNum < plannedNum;
}

/**
 * Aggregate income records by source within a cycle.
 * @param {Array<{ source?: string; source_id?: string; source_name?: string; amount?: string|number; recurrence?: string }>} records
 * @param {Array<{ id: string; name?: string; recurrence?: string; category?: string; color?: string; icon?: string; is_active?: boolean }>} sources
 * @param {number} cycleTotal
 */
export function aggregateReceivedBySource(records = [], sources = [], cycleTotal = 0) {
  const sourceById = new Map();
  sources.forEach((source) => {
    if (source.is_active === false) return;
    sourceById.set(String(source.id), source);
  });

  const totals = new Map();
  records.forEach((record) => {
    const sourceId = String(record.source ?? record.source_id ?? '');
    if (!sourceId) return;
    const prev = totals.get(sourceId) || 0;
    totals.set(sourceId, prev + Number(record.amount || 0));
  });

  const total = cycleTotal > 0 ? cycleTotal : [...totals.values()].reduce((a, b) => a + b, 0);

  const rows = [...totals.entries()]
    .map(([sourceId, amount]) => {
      const meta = sourceById.get(sourceId) || {};
      const recurrence =
        meta.recurrence || records.find((r) => String(r.source ?? r.source_id) === sourceId)?.recurrence;
      return {
        sourceId,
        name: meta.name || records.find((r) => String(r.source ?? r.source_id) === sourceId)?.source_name || '',
        recurrence: recurrence || 'monthly_variable',
        category: meta.category,
        color: meta.color,
        icon: meta.icon,
        amount,
        pctOfTotal: total > 0 ? (amount / total) * 100 : 0,
        isFixed: isFixedRecurrence(recurrence),
      };
    })
    .filter((row) => row.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  return rows;
}

/**
 * Split received totals into fixed (guaranteed) vs variable (uncertain).
 */
export function splitFixedVariableTotals(bySourceRows = []) {
  let fixed = 0;
  let variable = 0;
  bySourceRows.forEach((row) => {
    if (row.isFixed) fixed += row.amount;
    else variable += row.amount;
  });
  return { fixed, variable };
}

/**
 * Group records by civil month (most recent month first).
 * @param {Array<{ received_on: string; amount?: string|number }>} records
 */
export function groupRecordsByMonth(records = [], locale = 'pt-PT') {
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  const dayFormatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  const groups = new Map();
  records.forEach((record) => {
    const dateISO = record.received_on;
    if (!dateISO) return;
    const monthKey = dateISO.slice(0, 7);
    if (!groups.has(monthKey)) {
      const date = new Date(`${dateISO}T12:00:00`);
      groups.set(monthKey, {
        monthKey,
        label: monthFormatter.format(date),
        total: 0,
        items: [],
      });
    }
    const group = groups.get(monthKey);
    const amount = Number(record.amount || 0);
    group.total += amount;
    group.items.push({
      ...record,
      shortDate: dayFormatter.format(new Date(`${dateISO}T12:00:00`)),
    });
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: group.items.sort((a, b) => (a.received_on < b.received_on ? 1 : -1)),
    }))
    .sort((a, b) => (a.monthKey < b.monthKey ? 1 : -1));
}
